import { CloudUpload, Sync, Warning } from '@mui/icons-material';
import { alpha, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useRef, useState } from 'react';
import { convertFileFormatToText } from '../../utils/string';
import { bytesConversion, minifyBytes, SiUnit } from '../../utils/conversion';

interface DragDropUploadProps {
	onFileSelect: (selectedFile: File) => void;
	allowedFormats: string[];
	helperText?: string;
	fileError: string;
	setFileError: (err: string) => void;
	subText?: string | string[];
	disabled?: boolean;
	maxSizeMB?: number;
	disabledReason?: string;
}

const DragDropUpload: React.FC<DragDropUploadProps> = ({
	onFileSelect,
	allowedFormats,
	helperText = '',
	fileError,
	setFileError,
	subText,
	disabled,
	disabledReason,
	maxSizeMB,
}) => {
	const theme = useTheme().palette;
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const ref = useRef<HTMLInputElement>(null);

	const curColour = isDragging ? 'action' : 'primary';

	const backgroundCol = alpha(fileError ? theme.error.main : disabled ? theme.action.disabled : theme.primary.main, 0.1);
	const borderCol = fileError ? 'error.main' : disabled ? 'action.disabled' : `${curColour}.main`;
	const mainTextCol = fileError ? 'error' : disabled ? 'action.disabled' : curColour;
	const subTextCol = fileError ? 'error' : disabled ? 'action.disabled' : 'textPrimary';

	const getIcon = () => {
		if (fileError) {
			return <Warning color="error" fontSize="large" sx={{ marginBottom: 2 }} />;
		} else if (disabled) {
			return <Sync color="disabled" fontSize="large" sx={{ marginBottom: 2 }} />;
		} else {
			return <CloudUpload color={curColour} fontSize="large" sx={{ marginBottom: 2 }} />;
		}
	};

	const submitFile = (selectedFile: File) => {
		if (ref.current) {
			ref.current.value = '';
		}

		if (selectedFile.type === '') {
			setFileError('Could not identify file type, make sure it is not a folder');
		} else if (!allowedFormats.includes('*') && !allowedFormats.includes(selectedFile.type)) {
			setFileError(`Selected file (${selectedFile.name}) has an invalid format (${convertFileFormatToText(selectedFile.type)})`);
		} else if (maxSizeMB && bytesConversion(selectedFile.size, SiUnit.MEGA) > maxSizeMB) {
			setFileError(
				`Selected file has size (${minifyBytes(selectedFile.size)}) which is greater than the limit (${minifyBytes(maxSizeMB)})`
			);
		} else {
			setFileError('');
			onFileSelect(selectedFile);
		}
	};

	const formatSubText = () => {
		if (subText) {
			if (typeof subText === 'string') {
				return <>{subText}</>;
			} else {
				if (subText.length === 1) {
					return <>{subText[0]}</>;
				} else {
					return (
						<>
							{subText[0]}
							{subText.slice(1).map((st) => {
								return (
									<>
										<br></br>
										{st}
									</>
								);
							})}
						</>
					);
				}
			}
		} else {
			return <></>;
		}
	};

	return (
		<Tooltip title={disabled ? disabledReason : undefined}>
			<div
				onDragEnter={(e) => {
					e.preventDefault();
					setIsDragging(true);
				}}
				onDragOver={(e) => e.preventDefault()}
				onDragLeave={(e) => {
					const { relatedTarget } = e;
					if (!relatedTarget || !e.currentTarget.contains(relatedTarget as Node)) {
						setIsDragging(false);
					}
				}}
				onDrop={(e) => {
					e.preventDefault();
					setIsDragging(false);

					if (!disabled && e.dataTransfer.files?.length > 0 && e.dataTransfer.files.item(0)) {
						submitFile(e.dataTransfer.files[0]);
					}
				}}
				onClick={() => {
					if (!disabled && ref?.current) {
						ref.current.click();
					}
				}}
				style={{ width: '100%', height: '100%' }}
				data-testid="file-upload-drag-and-drop-area"
				aria-disabled={disabled}
			>
				<input
					data-testid="file-upload-input"
					id="file-upload-input"
					type="file"
					ref={ref}
					hidden
					onChange={(e) => {
						if (e.target.files && e.target.files.length > 0) {
							submitFile(e.target.files[0]);
						}
					}}
					accept={allowedFormats.join(',')}
				/>
				<Paper
					sx={{
						borderStyle: 'dashed',
						borderWidth: 4,
						borderColor: borderCol,
						backgroundColor: backgroundCol,
					}}
					elevation={0}
				>
					<Tooltip title={helperText}>
						<Stack alignItems={'center'} paddingX={2} paddingY={4}>
							{getIcon()}

							<Typography variant="h5" component={'p'} color={mainTextCol} sx={{ userSelect: 'none' }} textAlign={'center'}>
								Drag and Drop or Click to Choose File to Upload
							</Typography>
							{subText && (
								<Typography variant="body1" color={subTextCol} sx={{ userSelect: 'none' }} textAlign={'center'}>
									{formatSubText()}
								</Typography>
							)}

							<Typography variant="body1" color={subTextCol} sx={{ userSelect: 'none' }} textAlign={'center'}>
								Supported file types:{' '}
								{Array.from(new Set(allowedFormats.map((ft) => `.${convertFileFormatToText(ft).toLocaleLowerCase()}`))).join(', ')}
							</Typography>

							{fileError && (
								<Typography color={'error'} paddingTop={4} width={'100%'} maxWidth={'100%'} data-testid="drag-and-drop-error-text">
									{fileError}
								</Typography>
							)}
						</Stack>
					</Tooltip>
				</Paper>
			</div>
		</Tooltip>
	);
};

export default DragDropUpload;
